import { createSlice, type Draft, type PayloadAction } from '@reduxjs/toolkit';

import {
  ASSET_PANELS_KEYS,
  LS_ASSETS_DEFAULT_PANELS,
  LS_SEARCH_DEFAULT_PANELS,
  SEARCH_PANELS_KEYS,
} from '../../constants/data';
import { setAssetDefaultPanels, setSearchDefaultPanels } from '../app/slice';
import type { PanelsAppState } from './types';

function savePanelsInLocalStorage(state: Record<string, boolean>): void {
  const panels = Object.entries(state)
    .filter(([, opened]) => opened)
    .map(([panelId]) => panelId);
  const searchPanels = panels.filter((panelId) =>
    SEARCH_PANELS_KEYS.includes(panelId),
  );
  const assetPanels = panels.filter((panelId) =>
    ASSET_PANELS_KEYS.includes(panelId),
  );
  localStorage.setItem(LS_SEARCH_DEFAULT_PANELS, JSON.stringify(searchPanels));
  localStorage.setItem(LS_ASSETS_DEFAULT_PANELS, JSON.stringify(assetPanels));
}

const openPanels = (
  state: Draft<PanelsAppState>,
  panelIds: readonly string[],
) => {
  panelIds.forEach((panelId) => {
    state.openedPanels[panelId] = true;
  });
};

const initialState: PanelsAppState = {
  openedPanels: {},
  isPanelsVisibleInFullscreen: false,
};

export const panelsSlice = createSlice({
  name: 'panels',
  initialState,
  reducers: {
    openPanel: (state, action: PayloadAction<string>) => {
      state.openedPanels[action.payload] = true;
      savePanelsInLocalStorage(state.openedPanels);
    },
    closePanelById: (state, action: PayloadAction<string>) => {
      state.openedPanels[action.payload] = false;
      savePanelsInLocalStorage(state.openedPanels);
    },
    togglePanelsInFullscreenMode: (state) => {
      state.isPanelsVisibleInFullscreen = !state.isPanelsVisibleInFullscreen;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setAssetDefaultPanels, (state, action) => {
        openPanels(state, action.payload);
      })
      .addCase(setSearchDefaultPanels, (state, action) => {
        openPanels(state, action.payload);
      });
  },
});

export const { openPanel, closePanelById, togglePanelsInFullscreenMode } =
  panelsSlice.actions;

export const panelsReducer = panelsSlice.reducer;
